/**
 * actionExecutor.js — Shared action executor for Voice Dump + AI Coach.
 *
 * Executes structured actions against Zustand stores.
 * Both Voice Dump and AI Coach feed actions through this utility.
 */

import useTaskStore from '../stores/useTaskStore';
import useProjectStore from '../stores/useProjectStore';
import useAreaStore from '../stores/useAreaStore';
import useResourceStore from '../stores/useResourceStore';
import useFlowStore from '../stores/useFlowStore';
import useTimerStore from '../stores/useTimerStore';
import useIdentityStore from '../stores/useIdentityStore';

// ---------- Entity Lookup Helpers ----------

/**
 * Fuzzy-find an entity by name/title field.
 * Matching priority:
 *   1. Exact ID match (when AI uses task IDs from context)
 *   2. Exact case-insensitive title/name match
 *   3. Includes match (entity name contains search or vice versa)
 *   4. Word-tokenized match (all search words appear in entity name)
 */
export function findEntity(entities, searchName, field, matchId) {
  if (!entities) return null;

  // 1. Exact ID match (highest confidence)
  if (matchId) {
    const idMatch = entities.find(e => e.id === matchId);
    if (idMatch) return idMatch;
  }

  if (!searchName) return null;
  const search = searchName.toLowerCase().trim();

  // 2. Exact case-insensitive match
  let found = entities.find(e => e[field]?.toLowerCase().trim() === search);
  if (found) return found;

  // 3. Includes match
  found = entities.find(e => e[field]?.toLowerCase().includes(search));
  if (found) return found;

  // Reverse includes (search term includes entity name)
  found = entities.find(e => search.includes(e[field]?.toLowerCase().trim()));
  if (found) return found;

  // 4. Word-tokenized match — all search words appear in entity name
  const searchWords = search.split(/\s+/).filter(w => w.length > 1);
  if (searchWords.length > 0) {
    found = entities.find(e => {
      const entityName = e[field]?.toLowerCase() || '';
      return searchWords.every(word => entityName.includes(word));
    });
    if (found) return found;
  }

  return null;
}

export function findEntityId(entities, searchName, field) {
  const entity = findEntity(entities, searchName, field);
  return entity?.id || null;
}

// ---------- Single Action Executor ----------

/**
 * Execute a single action against the appropriate store.
 * @param {{ type: string, data: object }} action
 * @returns {{ success: boolean, message: string }}
 */
export async function executeAction(action) {
  const { type, data } = action;

  // AI may use inconsistent field names — normalize for task matching
  const resolveTaskName = () => data.matchTitle || data.title || data.taskTitle || data.name;
  const resolveMatchId = () => data.matchId || data.taskId;

  switch (type) {
    // --- Tasks ---
    case 'create_task': {
      const store = useTaskStore.getState();
      const projectId = data.projectName
        ? findEntityId(useProjectStore.getState().projects, data.projectName, 'name')
        : null;
      const areaId = data.areaName
        ? findEntityId(useAreaStore.getState().areas, data.areaName, 'name')
        : null;

      store.addTask({
        title: data.title,
        priority: data.priority || 'medium',
        dueDate: data.dueDate || null,
        scheduledDate: data.scheduledDate || null,
        projectId,
        areaId,
        status: 'todo',
      });

      // Add subtasks if any
      if (data.subtasks?.length > 0) {
        const tasks = useTaskStore.getState().tasks;
        const parentTask = tasks[tasks.length - 1]; // Just created
        if (parentTask) {
          data.subtasks.forEach(st => {
            store.addSubtask(parentTask.id, st);
          });
        }
      }
      return { success: true, message: `Created task "${data.title}"` };
    }

    case 'update_task': {
      const store = useTaskStore.getState();
      const taskName = resolveTaskName();
      const task = findEntity(store.tasks, taskName, 'title', resolveMatchId());
      if (!task) throw new Error(`Task "${taskName}" not found`);

      if (data.updates?.status === 'done') {
        store.completeTask(task.id);
      }
      if (data.updates?.priority) {
        store.updateTask(task.id, { priority: data.updates.priority });
      }
      if (data.updates?.dueDate) {
        store.updateTask(task.id, { dueDate: data.updates.dueDate });
      }
      if (data.updates?.scheduledDate) {
        store.updateTask(task.id, { scheduledDate: data.updates.scheduledDate });
      }
      if (data.updates?.projectName) {
        const projectId = findEntityId(useProjectStore.getState().projects, data.updates.projectName, 'name');
        if (projectId) store.updateTask(task.id, { projectId });
      }
      if (data.updates?.areaName) {
        const areaId = findEntityId(useAreaStore.getState().areas, data.updates.areaName, 'name');
        if (areaId) store.updateTask(task.id, { areaId });
      }
      return { success: true, message: `Updated task "${task.title}"` };
    }

    case 'complete_task': {
      const store = useTaskStore.getState();
      const taskName = resolveTaskName();
      const task = findEntity(store.tasks, taskName, 'title', resolveMatchId());
      if (!task) throw new Error(`Task "${taskName}" not found`);
      if (task.status !== 'done') {
        store.completeTask(task.id);
      }
      return { success: true, message: `Completed task "${task.title}"` };
    }

    case 'delete_task': {
      const store = useTaskStore.getState();
      const taskName = resolveTaskName();
      const task = findEntity(store.tasks, taskName, 'title', resolveMatchId());
      if (!task) throw new Error(`Task "${taskName}" not found`);
      store.deleteTask(task.id);
      return { success: true, message: `Deleted task "${task.title}"` };
    }

    // --- Subtasks (micro-chunking) ---
    case 'add_subtasks': {
      const store = useTaskStore.getState();
      const taskName = data.taskTitle || data.matchTitle || data.title;
      const task = findEntity(store.tasks, taskName, 'title', resolveMatchId());
      if (!task) throw new Error(`Task "${taskName}" not found`);

      const subtasks = data.subtasks || [];
      subtasks.forEach(st => {
        store.addSubtask(task.id, {
          title: st.title,
          estimatedMinutes: st.estimatedMinutes || null,
        });
      });
      return { success: true, message: `Added ${subtasks.length} subtasks to "${task.title}"` };
    }

    case 'prioritize_task': {
      const store = useTaskStore.getState();
      const taskName = resolveTaskName();
      const task = findEntity(store.tasks, taskName, 'title', resolveMatchId());
      if (!task) throw new Error(`Task "${taskName}" not found`);
      store.updateTask(task.id, { priority: data.priority });
      return { success: true, message: `Set "${task.title}" priority to ${data.priority}` };
    }

    // --- Projects ---
    case 'create_project': {
      const store = useProjectStore.getState();
      const areaId = data.areaName
        ? findEntityId(useAreaStore.getState().areas, data.areaName, 'name')
        : null;
      store.addProject({
        name: data.name,
        areaId,
        dueDate: data.dueDate || null,
        status: 'active',
      });
      return { success: true, message: `Created project "${data.name}"` };
    }

    case 'update_project': {
      const store = useProjectStore.getState();
      const project = findEntity(store.projects, data.matchName, 'name');
      if (!project) throw new Error(`Project "${data.matchName}" not found`);
      store.updateProject(project.id, data.updates || {});
      return { success: true, message: `Updated project "${project.name}"` };
    }

    case 'delete_project': {
      const store = useProjectStore.getState();
      const project = findEntity(store.projects, data.matchName, 'name');
      if (!project) throw new Error(`Project "${data.matchName}" not found`);
      store.updateProject(project.id, { status: 'archived' });
      return { success: true, message: `Archived project "${project.name}"` };
    }

    // --- Areas ---
    case 'create_area': {
      const store = useAreaStore.getState();
      store.addArea({ name: data.name });
      return { success: true, message: `Created area "${data.name}"` };
    }

    case 'delete_area': {
      const store = useAreaStore.getState();
      const area = findEntity(store.areas, data.matchName, 'name');
      if (!area) throw new Error(`Area "${data.matchName}" not found`);
      store.deleteArea(area.id);
      return { success: true, message: `Deleted area "${area.name}"` };
    }

    // --- Resources ---
    case 'create_resource': {
      const store = useResourceStore.getState();
      const projectId = data.projectName
        ? findEntityId(useProjectStore.getState().projects, data.projectName, 'name')
        : null;
      const areaId = data.areaName
        ? findEntityId(useAreaStore.getState().areas, data.areaName, 'name')
        : null;
      store.addResource({
        title: data.title,
        type: data.type || 'note',
        projectId,
        areaId,
        tags: data.tags || [],
        status: 'active',
      });
      return { success: true, message: `Created resource "${data.title}"` };
    }

    case 'delete_resource': {
      const store = useResourceStore.getState();
      const resource = findEntity(store.resources, data.matchTitle, 'title');
      if (!resource) throw new Error(`Resource "${data.matchTitle}" not found`);
      store.updateResource(resource.id, { status: 'archived' });
      return { success: true, message: `Archived resource "${resource.title}"` };
    }

    // --- Flows ---
    case 'create_flow': {
      const { applyCircuitDraft } = await import('./circuitDraft.js');
      const flow = applyCircuitDraft(data);
      const count = flow?.steps?.length ?? 0;
      return { success: true, message: `Created flow "${data.name}" with ${count} steps` };
    }

    case 'delete_flow': {
      const store = useFlowStore.getState();
      const circuit = findEntity(store.flows, data.matchName, 'name');
      if (!circuit) throw new Error(`Flow "${data.matchName}" not found`);
      store.deleteCircuit(circuit.id);
      return { success: true, message: `Deleted flow "${circuit.name}"` };
    }

    // --- Timer ---
    case 'update_timer': {
      const store = useTimerStore.getState();
      if (data.focusDuration) store.setFocusDuration(data.focusDuration);
      if (data.restDuration) store.setRestDuration(data.restDuration);
      if (data.reflectDuration) store.setReflectDuration(data.reflectDuration);
      if (data.prepDuration) store.setPrepDuration(data.prepDuration);
      return { success: true, message: 'Updated timer settings' };
    }

    // --- Identities ---
    case 'create_identity': {
      const store = useIdentityStore.getState();
      const identity = {
        name: data.name,
        emoji: data.emoji || '🎭',
        description: data.description || '',
        color: data.color || 'categorical-2',
      };
      // Apply timer settings if provided
      if (data.settings) {
        identity.settings = {
          durations: {
            focus: data.settings.durations?.focus || 3000,
            rest: data.settings.durations?.rest || 600,
            reflect: data.settings.durations?.reflect || 300,
            prep: data.settings.durations?.prep || 120,
          },
          phases: {
            prep: data.settings.phases?.prep ?? true,
            reflect: data.settings.phases?.reflect ?? true,
            rest: data.settings.phases?.rest ?? true,
          },
        };
      }
      store.addIdentity(identity);
      return { success: true, message: `Created identity "${data.name}"` };
    }

    case 'update_identity': {
      const store = useIdentityStore.getState();
      const identity = findEntity(store.identities, data.matchName, 'name');
      if (!identity) throw new Error(`Identity "${data.matchName}" not found`);
      const updates = { ...data.updates };
      if (updates.settings) {
        // Deep merge settings
        updates.settings = {
          durations: { ...identity.settings?.durations, ...updates.settings.durations },
          phases: { ...identity.settings?.phases, ...updates.settings.phases },
        };
      }
      store.updateIdentity(identity.id, updates);
      return { success: true, message: `Updated identity "${identity.name}"` };
    }

    case 'delete_identity': {
      const store = useIdentityStore.getState();
      const identity = findEntity(store.identities, data.matchName, 'name');
      if (!identity) throw new Error(`Identity "${data.matchName}" not found`);
      store.deleteIdentity(identity.id);
      return { success: true, message: `Deleted identity "${identity.name}"` };
    }

    case 'set_active_identity': {
      const store = useIdentityStore.getState();
      const identity = findEntity(store.identities, data.matchName, 'name');
      if (!identity) throw new Error(`Identity "${data.matchName}" not found`);
      store.setActiveIdentity(identity.id);
      return { success: true, message: `Activated identity "${identity.name}"` };
    }

    default:
      throw new Error(`Unknown action type: ${type}`);
  }
}

// ---------- Batch Executor ----------

/**
 * Execute multiple actions in order, returning results.
 * Creates are executed before updates/deletes.
 */
/**
 * Execution order for AI actions.
 * Creates are executed in dependency order: areas/identities first (referenced by
 * projects/tasks), then projects (referenced by tasks), then tasks (referenced by
 * flows), then resources, then flows (which reference tasks by ID).
 */
export const ACTION_EXECUTION_ORDER = {
  create_area: 0,
  create_identity: 1,
  create_project: 2,
  create_task: 3,
  create_resource: 4,
  create_flow: 5,
};

export async function executeActions(actions) {
  const sorted = [...actions].sort((a, b) => {
    const oa = ACTION_EXECUTION_ORDER[a.type] ?? 10;
    const ob = ACTION_EXECUTION_ORDER[b.type] ?? 10;
    return oa - ob;
  });

  const results = { succeeded: [], failed: [] };

  for (const action of sorted) {
    try {
      const result = await executeAction(action);
      results.succeeded.push({ ...action, result });
    } catch (error) {
      results.failed.push({ action, error: error.message });
    }
  }

  return results;
}
